import { Prec, RangeSetBuilder } from '@codemirror/state';
import { Decoration, ViewPlugin } from '@codemirror/view';

import type { Extension } from '@codemirror/state';
import type { DecorationSet, ViewUpdate, EditorView } from '@codemirror/view';

const cssPropertyDecoration = Decoration.mark({ class: 'cm-css-property' });
const cssValueDecoration = Decoration.mark({ class: 'cm-css-value' });
const cssNumberDecoration = Decoration.mark({ class: 'cm-css-number' });
const cssUnitDecoration = Decoration.mark({ class: 'cm-css-unit' });
const cssColorDecoration = Decoration.mark({ class: 'cm-css-color' });

const STYLE_ATTR_PATTERN = /style\s*=\s*"([^"]*)"/g;
const CSS_DECLARATION_PATTERN = /([a-z-]+)\s*:\s*([^;]*)/g;
const CSS_NUMBER_UNIT_PATTERN = /(-?\d+\.?\d*)(px|em|rem|%|in|cm|mm|pt|vh|vw|vmin|vmax|fr|s|ms|deg|rad)?/g;
const CSS_COLOR_HEX_PATTERN = /#[0-9a-fA-F]{3,8}/g;

const cssInlineHighlighter = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = this.buildDecorations(view);
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = this.buildDecorations(update.view);
      }
    }

    buildDecorations(view: EditorView): DecorationSet {
      const builder = new RangeSetBuilder<Decoration>();
      const text = view.state.doc.toString();
      const matches: Array<{ from: number; to: number; decoration: Decoration }> = [];

      let styleMatch;
      STYLE_ATTR_PATTERN.lastIndex = 0;
      while ((styleMatch = STYLE_ATTR_PATTERN.exec(text)) !== null) {
        const styleContent = styleMatch[1];
        const styleStart = styleMatch.index + styleMatch[0].indexOf('"') + 1;

        CSS_DECLARATION_PATTERN.lastIndex = 0;
        let declMatch;
        while ((declMatch = CSS_DECLARATION_PATTERN.exec(styleContent)) !== null) {
          const propertyName = declMatch[1];
          const propertyValue = declMatch[2];
          const propertyStart = styleStart + declMatch.index;
          const valueStart = styleStart + declMatch.index + declMatch[0].indexOf(propertyValue);

          matches.push({
            from: propertyStart,
            to: propertyStart + propertyName.length,
            decoration: cssPropertyDecoration,
          });

          let hasTokenMatches = false;

          CSS_COLOR_HEX_PATTERN.lastIndex = 0;
          let colorMatch;
          while ((colorMatch = CSS_COLOR_HEX_PATTERN.exec(propertyValue)) !== null) {
            hasTokenMatches = true;
            matches.push({
              from: valueStart + colorMatch.index,
              to: valueStart + colorMatch.index + colorMatch[0].length,
              decoration: cssColorDecoration,
            });
          }

          CSS_NUMBER_UNIT_PATTERN.lastIndex = 0;
          let numMatch;
          while ((numMatch = CSS_NUMBER_UNIT_PATTERN.exec(propertyValue)) !== null) {
            hasTokenMatches = true;
            const numStart = valueStart + numMatch.index;
            const numberPart = numMatch[1];
            const unitPart = numMatch[2];

            matches.push({
              from: numStart,
              to: numStart + numberPart.length,
              decoration: cssNumberDecoration,
            });

            if (unitPart) {
              matches.push({
                from: numStart + numberPart.length,
                to: numStart + numberPart.length + unitPart.length,
                decoration: cssUnitDecoration,
              });
            }
          }

          if (!hasTokenMatches) {
            matches.push({
              from: valueStart,
              to: valueStart + propertyValue.trimEnd().length,
              decoration: cssValueDecoration,
            });
          }
        }
      }

      matches.sort((a, b) => a.from - b.from || b.to - a.to);

      let lastEnd = -1;
      for (const m of matches) {
        if (m.from >= lastEnd) {
          builder.add(m.from, m.to, m.decoration);
          lastEnd = m.to;
        }
      }

      return builder.finish();
    }
  },
  {
    decorations: (v) => v.decorations,
  },
);

export const createCssInlineHighlightExtension = (): Extension[] => {
  return [Prec.high(cssInlineHighlighter)];
};
